import type { APIFieldAttributes, LinkedComponent } from '@ardoq/api-types';
import { BubbleChartViewInterface } from '@ardoq/bubble-chart';
import type { EnhancedScopeData } from '@ardoq/data-model';
import { enhancedScopeDataOperations } from '@ardoq/scope-data';
import type { ReferenceWithBubbleComponent } from './types';

export type LinkedComponentWithReferenceTypeName = LinkedComponent & {
  referenceTypeName: string;
};

export const linkedComponentToReferenceInfoWithScopeData = <
  T extends LinkedComponent,
>(
  scopeData: EnhancedScopeData,
  linkedComponent: T
): (T & LinkedComponentWithReferenceTypeName) | null => {
  const { referenceId } = linkedComponent;
  const reference = scopeData.referencesById[referenceId];
  if (!reference) {
    return null;
  }

  const referenceType =
    enhancedScopeDataOperations.getReferenceTypeByReferenceId(
      scopeData,
      referenceId
    );
  if (!referenceType?.name) {
    return null;
  }

  return {
    ...linkedComponent,
    referenceTypeName: referenceType.name,
  };
};

export const linkedComponentToReferenceInfo = (
  viewInterface: BubbleChartViewInterface,
  linkedComponent: LinkedComponent
) => {
  const { referenceId } = linkedComponent;
  const rootWorkspaceId =
    viewInterface.getReferenceRootWorkspaceId(referenceId);
  const referenceTypeId = viewInterface.getReferenceTypeId(referenceId);
  if (rootWorkspaceId === null || referenceTypeId === null) {
    return null;
  }
  const referenceTypeName = viewInterface.getReferenceTypeById(
    rootWorkspaceId,
    referenceTypeId
  )?.name;
  if (!referenceTypeName) {
    return null;
  }
  return {
    ...linkedComponent,
    referenceTypeName,
  } satisfies LinkedComponentWithReferenceTypeName;
};

export const referenceFieldsWithScopeData = (
  scopeData: EnhancedScopeData,
  { referenceId }: LinkedComponentWithReferenceTypeName
) =>
  Object.values(
    enhancedScopeDataOperations.getReferenceFields(scopeData, referenceId)
  );

export const referencedComponentFieldsWithScopeData = (
  scopeData: EnhancedScopeData,
  { componentId }: LinkedComponentWithReferenceTypeName
) =>
  Object.values(
    enhancedScopeDataOperations.getComponentFields(scopeData, componentId)
  );

export const bubbleComponentFieldsWithScopeData = (
  scopeData: EnhancedScopeData,
  { bubbleComponentId }: ReferenceWithBubbleComponent
): APIFieldAttributes[] =>
  Object.values(
    enhancedScopeDataOperations.getComponentFields(scopeData, bubbleComponentId)
  );

export const referenceFields = (
  viewInterface: BubbleChartViewInterface,
  { referenceId }: LinkedComponentWithReferenceTypeName
) => viewInterface.getReferenceFields(referenceId);
export const referencedComponentFields = (
  viewInterface: BubbleChartViewInterface,
  { componentId }: LinkedComponentWithReferenceTypeName
) => viewInterface.getComponentFields(componentId);
